<template>
  <v-app>
    <v-main style="display: contents;">
      <div class="sweepstakes-confirmation">
        <v-container class="py-8">
          <!-- Success Header -->
          <v-row justify="center" class="mb-8">
            <v-col cols="12" md="8" class="text-center">
              <div class="success-animation mb-6">
                <v-icon size="120" color="success" class="success-icon">
                  mdi-check-circle
                </v-icon>
              </div>
              
              <h1 class="success-title mb-4">You're In!</h1>
              <p class="success-subtitle">
                Your sweepstakes entry has been successfully submitted
              </p>
            </v-col>
          </v-row>

          <!-- Entry Details -->
          <v-row justify="center" v-if="entry">
            <v-col cols="12" lg="8">
              <v-card class="entry-details" elevation="8">
                <v-card-title class="details-header">
                  <h3>Entry Confirmation</h3>
                </v-card-title>
                
                <v-card-text class="pa-6">
                  <!-- Entry Summary -->
                  <div class="entries-highlight mb-6">
                    <div class="entries-number">{{ formatNumber(entry.total_entries) }}</div>
                    <div class="entries-label">Total Entries</div>
                    <div v-if="entry.bonus_entries > 0" class="bonus-text">
                      Including {{ formatNumber(entry.bonus_entries) }} bonus entries!
                    </div>
                  </div>

                  <!-- Entry Information -->
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="detail-section">
                        <h4 class="section-title">Entry Information</h4>
                        
                        <div class="detail-item">
                          <span class="detail-label">Reference Number:</span>
                          <span class="detail-value reference-number">{{ entry.entry_reference }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Campaign:</span>
                          <span class="detail-value">{{ entry.campaign ? entry.campaign.title : '' }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Entry Tier:</span>
                          <span class="detail-value">{{ entry.tier ? entry.tier.name : '' }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Amount:</span>
                          <span class="detail-value">${{ formatPrice(entry.amount_paid) }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Entry Date:</span>
                          <span class="detail-value">{{ formatDate(entry.created_at) }}</span>
                        </div>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <div class="detail-section">
                        <h4 class="section-title">Contact Information</h4>
                        
                        <div class="detail-item">
                          <span class="detail-label">Name:</span>
                          <span class="detail-value">{{ entry.first_name }} {{ entry.last_name }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Email:</span>
                          <span class="detail-value">{{ entry.email }}</span>
                        </div>
                        
                        <div class="detail-item">
                          <span class="detail-label">Phone:</span>
                          <span class="detail-value">{{ entry.phone }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Prize Information -->
          <v-row justify="center" class="mt-6" v-if="entry && entry.campaign">
            <v-col cols="12" lg="8">
              <v-card class="prize-info" elevation="4">
                <v-card-text class="pa-6">
                  <div class="prize-section">
                    <v-icon size="48" color="warning" class="mb-4">mdi-trophy</v-icon>
                    <h3 class="prize-title">{{ entry.campaign.prize_description }}</h3>
                    <div v-if="entry.campaign.prize_value" class="prize-value">
                      Value: ${{ formatPrice(entry.campaign.prize_value) }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- What's Next -->
          <v-row justify="center" class="mt-6">
            <v-col cols="12" lg="8">
              <v-card class="next-steps" elevation="4">
                <v-card-title class="steps-header">
                  <h3>What's Next?</h3>
                </v-card-title>
                
                <v-card-text class="pa-6">
                  <v-timeline dense>
                    <v-timeline-item color="green" icon="fa fa-check" fill-dot>
                      <div class="timeline-content">
                        <h4>Entry Submitted ✓</h4>
                        <p>Your entry has been successfully recorded in our system.</p>
                      </div>
                    </v-timeline-item>
                    
                    <v-timeline-item color="info" icon="mdi-email" fill-dot>
                      <div class="timeline-content">
                        <h4>Confirmation Email</h4>
                        <p>Check your email for a detailed confirmation with all entry information.</p>
                      </div>
                    </v-timeline-item>
                    
                    <v-timeline-item color="warning" icon="mdi-calendar" fill-dot>
                      <div class="timeline-content">
                        <h4>Winner Announcement</h4>
                        <p v-if="entry && entry.campaign">
                          The winner will be announced after {{ formatDate(entry.campaign.end_date) }}.
                        </p>
                      </div>
                    </v-timeline-item>
                    
                    <v-timeline-item color="primary" icon="mdi-phone" fill-dot>
                      <div class="timeline-content">
                        <h4>Winner Contact</h4>
                        <p>If you win, we'll contact you directly via email and phone.</p>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <v-row justify="center" class="mt-8">
            <v-col cols="12" md="6" lg="4" class="text-center">
              <v-btn
                large
                color="grey"
                dark
                @click="$router.push('/sweepstakes')"
                class="action-btn mb-4"
                block
              >
                Back to Sweepstakes
              </v-btn>
              
              <v-btn
                large
                outlined
                color="primary"
                @click="shareEntry"
                class="action-btn"
                block
              >
                <v-icon left>mdi-share</v-icon>
                Share with Friends
              </v-btn>
            </v-col>
          </v-row>

          <!-- Loading State -->
          <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
        </v-container>
      </div>
    </v-main>
  </v-app>
</template>

<script>

export default {
  name: 'SweepstakesConfirmation',
  props: {
    entryId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      entry: null
    }
  },
  async mounted() {
    await this.loadEntry()
  },
  methods: {
    async loadEntry() {
      try {
        this.loading = true
        
        const response = await window.axios.get(`api/sweepstakes/entry/${this.entryId}`)
        
        if (response.data.flag === 1) {
          this.entry = response.data.entry
        } else {
          throw new Error('Entry not found')
        }
        
      } catch (error) {
        console.error('Error loading entry:', error)
        this.$toast.error('Failed to load entry information')
        this.$router.push('/sweepstakes')
      } finally {
        this.loading = false
      }
    },
    
    shareEntry() {
      if (navigator.share) {
        navigator.share({
          title: 'I just entered a sweepstakes!',
          text: `I just entered the ${this.entry?.campaign?.title} sweepstakes. Check it out!`,
          url: window.location.origin + '/sweepstakes'
        })
      } else {
        // Fallback for browsers that don't support Web Share API
        const url = window.location.origin + '/sweepstakes'
        navigator.clipboard.writeText(url).then(() => {
          this.$toast.success('Sweepstakes link copied to clipboard!')
        })
      }
    },
    
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    
    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value)
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.sweepstakes-confirmation {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.success-animation {
  animation: bounce 1s ease-in-out;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.success-icon {
  filter: drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3));
}

.success-title {
  font-size: 3rem;
  font-weight: bold;
  color: #4CAF50;
}

.success-subtitle {
  font-size: 1.3rem;
  color: #666;
}

.entry-details,
.prize-info,
.next-steps {
  border-radius: 12px;
}

.details-header,
.steps-header {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 16px 24px;
}

.entries-highlight {
  text-align: center;
  background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
  border-radius: 12px;
  padding: 24px;
  border: 2px solid #4CAF50;
}

.entries-number {
  font-size: 3.5rem;
  font-weight: bold;
  color: #4CAF50;
  line-height: 1;
}

.entries-label {
  font-size: 1.2rem;
  color: #333;
  margin-top: 8px;
}

.bonus-text {
  color: #FF9800;
  font-weight: bold;
  margin-top: 8px;
}

.detail-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  border-bottom: 2px solid #4CAF50;
  padding-bottom: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.detail-label {
  color: #666;
  font-weight: 500;
}

.detail-value {
  font-weight: bold;
  color: #333;
}

.reference-number {
  font-family: monospace;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
}

.prize-section {
  text-align: center;
}

.prize-title {
  color: #333;
  margin-bottom: 8px;
}

.prize-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #FF9800;
}

.timeline-content h4 {
  color: #333;
  margin-bottom: 4px;
}

.timeline-content p {
  color: #666;
  margin-bottom: 0;
}

.action-btn {
  font-size: 1.1rem;
  border-radius: 50px;
  text-transform: none;
  font-weight: bold;
}

@media (max-width: 768px) {
  .success-title {
    font-size: 2.5rem;
  }
  
  .success-subtitle {
    font-size: 1.1rem;
  }
  
  .entries-number {
    font-size: 2.5rem;
  }
  
  .detail-item {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
